import { _ } from "~/i18n";
import Dialog from "~/ui/dialog";
import { createPremiumIcon, getPremiumIconSrc, node, text } from "~/util/dom";
import { ARROW_BACK_APP, ERROR_20, GROUP_20, REDEEM_20, VOUCHER_20 } from "~/ui/sds-compatible-icons";
import createSDSIcon from "../icon";
import { getCoupon, ICouponData, ICouponError, ICouponError403, payComplete } from "~/util/providers/premium";
import { store } from "~/state/store";
import { CLOSE_24 } from "@sznds/icons";
import { IUserSubscriptions } from "~common/types/user";
import { CONFIG } from "~/config";
import { onPremium } from "~/util/feedback";

interface ICouponActivationDialogProps {
	couponId?: string;
	onClose: () => void;
	onBack: () => void;
	onFinished: (data: IUserSubscriptions) => void;
}

const DEFAULT_PROPS: ICouponActivationDialogProps = {
	onClose: () => {},
	onBack: () => {},
	onFinished: () => {},
};
const REQUEST_TIMEOUT = 60 * 1000;

function setErrorMessage(message: string, errorMessage: HTMLElement) {
	errorMessage.innerHTML = "";
	errorMessage.append(createSDSIcon({ symbol: ERROR_20 }), text(message));
}

function evaluateError(data: ICouponError403, status: 400 | 403 | 404 | 429 | 503 | 401 | 500): {
	errorMessage: string;
	fatalError: boolean;
} {
	let errorMessage = "";
	let fatalError = false;

	switch (status) {
		// request validation error
		case 400:
			errorMessage = _("premium.coupon.error.somethingWentWrong");
			break;


		// forbidden, viz errors[0].status
		case 403: {
			if (data?.errors?.length) {
				switch (data.errors[0].status) {
					case "donor_disabled":
						errorMessage = _("premium.coupon.error.donorDisabled");
						fatalError = true;
						break;

					// kupon uz je pouzity
					case "used":
						errorMessage = _("premium.coupon.error.used");
						fatalError = true;
						break;

					// kupon uz neplati
					case "expired":
						errorMessage = _("premium.coupon.error.expired", { date: new Intl.DateTimeFormat(CONFIG.defaultLang).format(new Date(data.coupon?.validTo)) });
						fatalError = true;
						break;

					// kupon jeste neplati
					case "pending":
						errorMessage = _("premium.coupon.error.pending", { date: new Intl.DateTimeFormat(CONFIG.defaultLang).format(new Date(data.coupon?.validFrom)) });
						fatalError = true;
						break;

					case "cancelled":
						errorMessage = _("premium.coupon.error.cancelled");
						fatalError = true;
						break;

					default:
						errorMessage = _("premium.coupon.error.somethingWentWrong");
						break;
				}
			}
			break;
		}

		// coupon not found
		case 404:
			errorMessage = _("premium.coupon.error.notFound");
			break;

		// zkousi to moc brzy
		case 429:
			errorMessage = _("premium.coupon.error.earlyRetry");
			break;

		// network error
		case 503:
			errorMessage = _("premium.coupon.error.networkError");
			break;

		// unauthorized - to nastane asi jedine, pokud se odhlasi v jinem okne
		//case 401:
		// server error a cokoliv, co nemame podchycene
		//case 500:
		default:
			errorMessage = _("premium.coupon.error.somethingWentWrong");
			break;
	}

	return {
		errorMessage,
		fatalError,
	};
}

async function loadCouponData({
	code = "",
	id = "",
}): Promise<{
	data: ICouponData | ICouponError | ICouponError403;
	errorMessage?: string;
	fatalError?: boolean;
	status: number;
}> {
	try {
		const { data, status } = await getCoupon(code ? { code } : { id });

		if (status === 200) {
			return {
				data,
				status,
			};
		}

		return {
			...evaluateError(data as ICouponError403, status),
			data,
			status,
		};
	} catch (exception) {
		// eslint-disable-next-line no-console
		console.error(exception);

		return {
			errorMessage: _("premium.coupon.error.somethingWentWrong"),
			fatalError: false,
			data: null,
			status: 500,
		};
	}
}

export default class CouponActivationDialog {
	// instance dialogu
	#dialog: Dialog;

	// vstupní parametry
	#props: ICouponActivationDialogProps;

	// aktualne zadany kod kuponu
	#currentCode: string;

	// na ktere jsme strance
	#activePage: number;

	// je zobrazený prompt pro zavření dialogu?
	#promptCloseVisible: boolean;

	// token ziskany z id nebo code
	#couponData: ICouponData;

	// smi zaviraci tlacitko rovnou zavrit dialog?
	#directCloseEnabled: boolean;

	constructor(props: ICouponActivationDialogProps = DEFAULT_PROPS) {
		this.#props = {
			...DEFAULT_PROPS,
			...props,
		};
		this.#couponData = null;
		this.#promptCloseVisible = false;
		this.#directCloseEnabled = true;
		this.#currentCode = "";
		this.#dialog = new Dialog({
			className: "couponActivationDialog",
			onClose: () => {
				this.#props.onClose();
				this.destroy();
			},
			bgClose: false,
			bindEsc: false,
			onClickOut: () => {
				this.#promptClose();
			},
			onEscape: () => {
				this.#promptClose();
			},
		});

		// tlacitko zpet
		const backButton = node("button", { type: "button", className: "couponActivationDialog__backButton" });

		backButton.appendChild(createSDSIcon({ symbol: ARROW_BACK_APP }));
		backButton.addEventListener("click", event => {
			event.preventDefault();

			if (this.#activePage === 1) {
				this.#props.onBack();
				this.destroy();
			} else {
				this.#setActivePage(1);
			}
		});

		// tlacitko zavrit
		const closeButton = node("button", { type: "button", className: "couponActivationDialog__closeButton" });

		closeButton.appendChild(createSDSIcon({ symbol: CLOSE_24 }));
		closeButton.addEventListener("click", event => {
			event.preventDefault();
			this.#promptClose();
		});

		if (this.#props.couponId) {
			this.#dialog.dom.container.append(closeButton);
		} else {
			this.#dialog.dom.container.append(backButton, closeButton);
		}

		// tlacitko pro zavirani z dialogu nechceme, protoze to ma svou logiku, ktera se nehodi
		this.#dialog.dom.btnClose.remove();
		this.#dialog.open();

		// zaciname s id z URL, takze rovnou nacteme data kuponu a dal pokracujeme teprve s nimi
		if (this.#props.couponId) {
			(async () => {
				const couponData = await loadCouponData({
					id: this.#props.couponId,
				});

				if (couponData.status === 200) {
					this.#couponData = couponData.data as ICouponData;
					this.#currentCode = this.#couponData.code;
					this.#setActivePage(2);
				} else {
					this.#setActivePage(1, couponData);
				}
			})();
		// jdeme cestou vyplnim kod - potvrdim kod
		} else {
			this.#setActivePage(1);
		}
	}

	destroy() {
		this.#dialog?.destroy();
		this.#dialog = null;
	}

	#setActivePage(newPage, initialCouponData?: Awaited<ReturnType<typeof loadCouponData>>) {
		if (newPage === 1) {
			this.#dialog.dom.content.innerHTML = "";
			this.#dialog.dom.content.append(this.#buildCodePage(initialCouponData));
		} else {
			this.#dialog.dom.content.innerHTML = "";
			this.#dialog.dom.content.append(this.#buildAccountPage());
		}

		this.#activePage = newPage;
	}

	#buildCodePage(initialCouponData?: Awaited<ReturnType<typeof loadCouponData>>) {
		const codePage = node("form", { action: "/", className: "couponActivationDialog__codePage" });
		const inputValue = (initialCouponData?.data as ICouponError403)?.coupon?.code || this.#currentCode;
		const codeInput = node("input", { type: "text", value: inputValue, disabled: initialCouponData?.fatalError || !!this.#props.couponId, className: "couponActivationDialog__codeInput" });
		const continueButton = node("button", { type: "submit", disabled: inputValue.length === 0, className: "couponActivationDialog__actionButton" }, _("premium.coupon.continueButton"));
		const infoContainer = node("div", { className: "couponActivationDialog__infoContainer" });
		const errorMessage = node("div", { className: "couponActivationDialog__errorMessage" });
		const donorInfo = node("div", { className: "couponActivationDialog__info" });
		const label = node("label", { className: "couponActivationDialog__label" });
		const errorCloseButton = node("button", { type: "button", className: "couponActivationDialog__actionButton couponActivationDialog__actionButton--close" }, _("premium.coupon.closeButton"));
		const stepIndicator = node("div", { className: "couponActivationDialog__stepIndicator", innerHTML: `<div class="couponActivationDialog__stepIndicator__line couponActivationDialog__stepIndicator__line--active"></div><div class="couponActivationDialog__stepIndicator__line"></div>` });
		const scrollable = node("div", { className: "couponActivationDialog__scrollable" });
		const loader = node("mapy-loader");
		const setCouponData = (couponData: Awaited<ReturnType<typeof loadCouponData>>) => {
			scrollable.append(infoContainer);
			this.#directCloseEnabled = couponData.fatalError;
			setErrorMessage(couponData.errorMessage, errorMessage);
			this.#couponData = null;
			infoContainer.innerHTML = "";

			if (couponData.fatalError) {
				codeInput.disabled = true;
				loader.replaceWith(errorCloseButton);
			} else {
				if (couponData.status === 429) {
					// eslint-disable-next-line require-atomic-updates
					continueButton.disabled = true;
					continueButton.classList.add("couponActivationDialog__actionButton--loading");
					// eslint-disable-next-line require-atomic-updates
					codeInput.disabled = true;
					setTimeout(() => {
						continueButton.disabled = false;
						continueButton.classList.remove("couponActivationDialog__actionButton--loading");
						errorMessage.remove();

						if (!this.#props.couponId) {
							codeInput.disabled = false;
						}
					}, REQUEST_TIMEOUT);
				}
				loader.replaceWith(continueButton);
			}

			if ((couponData.data as ICouponError403)?.coupon?.donor?.name) {
				const span = node("span");

				donorInfo.innerHTML = "";
				span.append(text(`${_("premium.coupon.donor")} `), (couponData.data as ICouponError403).coupon.donor.tocUrl
					? node("a", { href: (couponData.data as ICouponError403).coupon.donor.tocUrl, target: "_blank" }, (couponData.data as ICouponError403).coupon.donor.name)
					: text((couponData.data as ICouponError403).coupon.donor.name));
				donorInfo.append(createSDSIcon({ symbol: REDEEM_20 }), span);
				infoContainer.append(donorInfo);
			} else {
				donorInfo.remove();
			}

			infoContainer.append(errorMessage);
		};

		errorCloseButton.addEventListener("click", event => {
			event.preventDefault();
			this.#promptClose();
		});

		codePage.addEventListener("submit", async event => {
			event.preventDefault();

			continueButton.replaceWith(loader);

			// pokud mame do zacatku id, cely input ignorujeme a pracujeme jen s id
			const couponData = await loadCouponData(this.#props.couponId
				? {
					id: this.#props.couponId,
				}
				: {
					code: codeInput.value,
				});

			if (couponData.status === 200) {
				this.#couponData = couponData.data as ICouponData;
				this.#currentCode = this.#couponData.code;
				this.#setActivePage(2);
			} else {
				setCouponData(couponData);
			}
		});
		!this.#props.couponId && codeInput.addEventListener("input", () => {
			continueButton.disabled = codeInput.value.length === 0;
			this.#currentCode = codeInput.value;
			this.#directCloseEnabled = codeInput.value.length === 0;
		});

		// nadpis
		const header = node("h1", { className: "couponActivationDialog__header" });
		const header1 = node("span", {}, _("premium.coupon.title1"));
		const header2 = node("span", { className: "couponActivationDialog__header__premium" }, _("premium.coupon.title2"));

		header2.prepend(createPremiumIcon());
		header.append(header1, header2);

		// input s labelem
		label.append(node("span", {}, _("premium.coupon.codeInputLabel")), codeInput);
		scrollable.append(header, label, infoContainer);
		codePage.append(stepIndicator, scrollable, initialCouponData && initialCouponData.fatalError ? errorCloseButton : continueButton, node("p", { className: "couponActivationDialog__bottomError" }));

		if (initialCouponData) {
			setCouponData(initialCouponData);
		}

		return codePage;
	}

	#buildAccountPage() {
		const { user } = store.getState().user;
		const accountPage = node("div", { className: "couponActivationDialog__accountPage" });
		const useTokenButton = node("button", { type: "button", className: "couponActivationDialog__actionButton" }, _("premium.coupon.useTokenButton"));
		const errorCloseButton = node("button", { type: "button", className: "couponActivationDialog__actionButton couponActivationDialog__actionButton--close" }, _("premium.coupon.closeButton"));
		const stepIndicator = node("div", { className: "couponActivationDialog__stepIndicator", innerHTML: `<div class="couponActivationDialog__stepIndicator__line"></div><div class="couponActivationDialog__stepIndicator__line couponActivationDialog__stepIndicator__line--active"></div>` });
		const scrollable = node("div", { className: "couponActivationDialog__scrollable" });
		const bottomError = node("p", { className: "couponActivationDialog__bottomError" });

		errorCloseButton.addEventListener("click", event => {
			event.preventDefault();
			this.#promptClose();
		});

		useTokenButton.addEventListener("click", async event => {
			event.preventDefault();
			const loader = node("mapy-loader");

			useTokenButton.replaceWith(loader);
			const { status, data } = await payComplete(this.#couponData.token, this.#couponData.paymentProvider);

			if (status === 200) {
				loader.replaceWith(useTokenButton);
				this.#props.onFinished(data);
			} else {
				const { errorMessage, fatalError } = evaluateError(data as unknown as ICouponError403, status as 400 | 403 | 404 | 429 | 503 | 401 | 500);

				loader.replaceWith(fatalError ? errorCloseButton : useTokenButton);
				bottomError.innerHTML = "";
				bottomError.append(text(errorMessage));
				this.#directCloseEnabled = fatalError;
			}
		});

		// info o kuponu
		const infoContainer = node("div", { className: "couponActivationDialog__infoContainer" });
		const donorInfo = node("div", { className: "couponActivationDialog__info" });
		const expirationInfo = node("div", { className: "couponActivationDialog__info" });
		const donorSpan = node("span");
		const expirationSpan = node("span", { innerHTML: _("premium.coupon.expiration", { date: new Intl.DateTimeFormat(CONFIG.defaultLang).format(new Date(this.#couponData.validTo)) }) });

		donorSpan.append(text(`${_("premium.coupon.donor")} `), this.#couponData.donor.tocUrl
			? node("a", { href: this.#couponData.donor.tocUrl, target: "_blank" }, this.#couponData.donor.name)
			: text(this.#couponData.donor.name));
		donorInfo.append(createSDSIcon({ symbol: REDEEM_20 }), donorSpan);
		expirationInfo.prepend(createSDSIcon({ symbol: VOUCHER_20 }), expirationSpan);
		infoContainer.append(donorInfo, expirationInfo);

		// multilicence
		if (this.#couponData.availableLicenceCount - this.#couponData.usedLicenceCount > 1) {
			const multiLicenceInfo = node("div", { className: "couponActivationDialog__info" });
			const multiLicenceSpan = node("span", { innerHTML: _("premium.coupon.multilicence", { count: this.#couponData.availableLicenceCount - this.#couponData.usedLicenceCount }) });

			multiLicenceInfo.append(createSDSIcon({ symbol: GROUP_20 }), multiLicenceSpan);
			infoContainer.append(multiLicenceInfo);
		}

		// info o uzivateli
		const userSection = node("div", { className: "couponActivationDialog__userSection" });
		const userInfo = node("div", { className: "couponActivationDialog__userInfo" });
		const avatar = node("mapy-avatar", { premium: user.premium ? "mapy" : "none", size: 48 });
		const userTexts = node("div", { className: "couponActivationDialog__userTexts" });
		const helpParagraph = node("p", { innerHTML: _("premium.coupon.userHelp", { start: `<button type="button" class="couponActivationDialog__helpButton">`, end: "</button>" }) });
		const helpButton = helpParagraph.querySelector(".couponActivationDialog__helpButton");

		helpButton?.addEventListener("click", event => {
			event.preventDefault();
			onPremium();
			this.#props.onClose();
			this.destroy();
		});
		avatar.append(node("img", { ...user.avatar ? { src: user.avatar } : {}, crossOrigin: "anonymous" }));
		userTexts.append(
			node("span", {}, user.name || ""),
			node("span", {}, user.accountDisplayName || ""),
		);
		userInfo.append(avatar, userTexts);

		// uzivatel uz je prihlaseny
		// eslint-disable-next-line no-negated-condition
		if (user.premium) {
			const alreadyPremiumContainer = node("div", { className: "couponActivationDialog__alreadyPremium" });
			const logo = `<img src="${getPremiumIconSrc()}" crossorigin="anonymous" />`;
			const alreadyPremium = node("p", { innerHTML: _("premium.coupon.alreadyPremium", { start: `<span>`, end: `</span>`, logo }) });

			this.#directCloseEnabled = true;
			alreadyPremiumContainer.append(alreadyPremium);
			alreadyPremiumContainer.insertAdjacentHTML("beforeend", `<svg width="16" height="11" viewBox="0 0 16 11" fill="none"><path d="M0 0L6.3359 9.50385C7.12754 10.6913 8.87246 10.6913 9.6641 9.50385L16 0H0Z" /></svg>`);
			userSection.append(
				alreadyPremiumContainer,
				userInfo,
				helpParagraph,
			);
		} else {
			userSection.append(
				node("h2", {}, _("premium.coupon.userSubtitle")),
				userInfo,
				helpParagraph,
			);
		}

		// nadpis
		const header = node("h1", { className: "couponActivationDialog__header" });
		const header1 = node("span", {}, _("premium.coupon.title1"));
		const header2 = node("span", { className: "couponActivationDialog__header__premium" }, _("premium.coupon.title2"));

		header2.prepend(createPremiumIcon());
		header.append(header1, header2);

		scrollable.append(header, infoContainer, userSection);
		accountPage.append(stepIndicator, scrollable, user.premium ? errorCloseButton : useTokenButton, bottomError);

		return accountPage;
	}

	#promptClose() {
		if (this.#directCloseEnabled || this.#currentCode.length === 0) {
			this.#props.onClose();
			this.destroy();
		}

		// pokud uz je prompt videt, tak ho znovu nesestavujeme
		if (this.#promptCloseVisible) {
			return;
		}

		this.#promptCloseVisible = true;
		const cover = node("div", { className: "couponActivationDialog__closePromptCover" });
		const container = node("div", { className: "couponActivationDialog__closePromptContainer" });
		const buttonCancel = node("button", { type: "button" }, _("premium.coupon.cancelCloseButton"));
		const buttonConfirm = node("button", { type: "button", className: "couponActivationDialog__closePromptButton__close" }, _("premium.coupon.confirmCloseButton"));

		container.append(node("span", {}, _("premium.coupon.closePrompt")), buttonConfirm, buttonCancel);
		this.#dialog.dom.container.append(cover, container);
		buttonCancel.addEventListener("click", event => {
			event.preventDefault();
			this.#promptCloseVisible = false;
			container.remove();
			cover.remove();
		});
		buttonConfirm.addEventListener("click", event => {
			event.preventDefault();
			this.#props.onClose();
			this.destroy();
		});
	}
}
